import React, { useState, useEffect, useCallback } from "react";
import { api, fmtCurrency, errMsg, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, KpiCard } from "@/components/common/Primitives";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, CheckCircle2, Clock, Ban, DollarSign, Users2, Target, ShoppingCart, TrendingUp } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";

export default function PlatformAdmin() {
  const [m, setM] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [trials, setTrials] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/metrics").then(({ data }) => setM(data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/companies").then(({ data }) => setCompanies(data)).catch(() => {});
    api.get("/admin/plans").then(({ data }) => setPlans(data)).catch(() => {});
    api.get("/admin/trials").then(({ data }) => setTrials(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (cid, body) => {
    try { await api.put(`/admin/companies/${cid}`, body); toast.success("Company updated"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const extendTrial = async (cid) => {
    try { await api.post(`/admin/companies/${cid}/extend-trial`); toast.success("Trial extended by 7 days"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!m) return <div className="py-16 flex justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-sky-500" /></div>;

  return (
    <div>
      <PageHeader title="Platform Admin" subtitle="SaaS operator view across all tenant companies" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="admin-total-companies" label="Total Companies" value={m.total_companies} icon={Building2} accent="sky" />
        <KpiCard testid="admin-active" label="Active" value={m.active_companies} icon={CheckCircle2} accent="emerald" />
        <KpiCard testid="admin-trial" label="Trial" value={m.trial_companies} icon={Clock} accent="amber" />
        <KpiCard testid="admin-suspended" label="Suspended" value={m.suspended_companies} icon={Ban} accent="rose" />
        <KpiCard testid="admin-mrr" label="Monthly Recurring Revenue" value={m.mrr} currency icon={DollarSign} accent="emerald" />
        <KpiCard testid="admin-users" label="Total Users" value={m.total_users} icon={Users2} accent="slate" />
        <KpiCard testid="admin-leads" label="Total Leads" value={m.total_leads} icon={Target} accent="sky" />
        <KpiCard testid="admin-orders" label="Total Orders" value={m.total_orders} icon={ShoppingCart} accent="emerald" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="admin-active-trials" label="Active Trials" value={m.active_trials ?? 0} icon={Clock} accent="amber" />
        <KpiCard testid="admin-converted-trials" label="Converted Trials" value={m.converted_trials ?? 0} icon={CheckCircle2} accent="emerald" />
        <KpiCard testid="admin-expired-trials" label="Expired (unconverted)" value={m.expired_trials ?? 0} icon={Ban} accent="rose" />
        <KpiCard testid="admin-conversion-rate" label="Trial Conversion Rate" value={`${m.trial_conversion_rate ?? 0}%`} icon={TrendingUp} accent="sky" />
      </div>

      <Card className="border border-slate-200/80 rounded-xl shadow-sm overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-slate-100"><h3 className="font-semibold text-slate-800">Companies</h3></div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50/80">{["Company", "Plan", "Status", "Users", "Leads", "RFQs", "Orders", "MRR", "Created", ""].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider font-semibold text-slate-500 whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {companies.map((c) => (
                <TableRow key={c.id} data-testid={`admin-company-${c.id}`}>
                  <TableCell className="font-medium text-slate-800 whitespace-nowrap">{c.name}</TableCell>
                  <TableCell>
                    <Select value={c.plan_id} onValueChange={(v) => act(c.id, { plan_id: v })}>
                      <SelectTrigger className="h-8 w-32 text-xs bg-white" data-testid={`admin-plan-${c.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell><StatusBadge value={c.status} /></TableCell>
                  <TableCell>{c.users}</TableCell>
                  <TableCell>{c.leads}</TableCell>
                  <TableCell>{c.rfqs}</TableCell>
                  <TableCell>{c.orders}</TableCell>
                  <TableCell className="font-medium">{fmtCurrency(c.mrr)}</TableCell>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(c.created_at)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`admin-actions-${c.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        {c.status !== "suspended" ? (
                          <DropdownMenuItem onClick={() => act(c.id, { status: "suspended" })} className="text-rose-600" data-testid={`suspend-${c.id}`}>Suspend</DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => act(c.id, { status: "active" })} className="text-emerald-600" data-testid={`reactivate-${c.id}`}>Reactivate</DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => act(c.id, { status: "active" })} data-testid={`activate-${c.id}`}>Mark active</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Card className="border border-slate-200/80 rounded-xl shadow-sm overflow-hidden mb-6" data-testid="free-trials-section">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-500" />
          <h3 className="font-semibold text-slate-800">Free Trials</h3>
          <span className="text-xs text-slate-400 font-medium">({trials.length} active)</span>
        </div>
        <div className="overflow-x-auto">
          {trials.length === 0 ? (
            <p className="px-6 py-8 text-center text-sm text-slate-400">No active free trials right now.</p>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50/80">{["Company", "Admin Contact", "Days Remaining", "Trial Ends", ""].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider font-semibold text-slate-500 whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {trials.map((t) => (
                  <TableRow key={t.id} data-testid={`trial-row-${t.id}`}>
                    <TableCell className="font-medium text-slate-800 whitespace-nowrap">{t.name}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      <p className="text-sm text-slate-700">{t.admin_name || "—"}</p>
                      <p className="text-xs text-slate-400">{t.admin_email || "—"}</p>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${t.trial_expired ? "bg-rose-100 text-rose-700" : t.trial_days_left <= 2 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`trial-days-${t.id}`}>
                        {t.trial_expired ? "Expired" : `${t.trial_days_left} ${t.trial_days_left === 1 ? "day" : "days"}`}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(t.trial_ends_at)}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => extendTrial(t.id)} className="h-8 gap-1.5 border-sky-200 text-sky-700 hover:bg-sky-50" data-testid={`extend-trial-${t.id}`}>
                        <Clock className="h-3.5 w-3.5" /> Extend +7 days
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>

      <Card className="border border-slate-200/80 rounded-xl shadow-sm p-6">
        <h3 className="font-semibold text-slate-800 mb-4">Subscription Plans</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {plans.map((p) => (
            <div key={p.id} className="p-4 rounded-xl border border-slate-200">
              <p className="font-bold font-display">{p.name}</p>
              <p className="text-2xl font-bold font-display mt-1">{fmtCurrency(p.price)}</p>
              <p className="text-xs text-slate-500 mt-2">{p.max_users >= 9999 ? "Unlimited" : p.max_users} users · {p.max_products >= 9999 ? "Unlimited" : p.max_products} products</p>
              <p className="text-xs text-slate-500">{p.max_leads >= 9999 ? "Unlimited" : p.max_leads} leads/mo</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
